/** Loan history for a single item with duration and overdue calculations. */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { calcLoanDurationDays } from '@/lib/loanDuration';

export interface LoanHistoryEntry {
  id: string;
  borrower_id: string;
  borrower_name: string | null;
  borrowed_at: string;
  returned_at: string | null;
  duration_days: number;
  due_at: string | null;
  overdue_days: number | null;
  notes?: string | null;
  return_condition?: 'good' | 'minor_wear' | 'damaged' | null;
  return_notes?: string | null;
}

/**
 * Fetches the complete loan history for a single item, enriched with borrower
 * display names, loan duration in days, and overdue-day calculations.
 *
 * For active (unreturned) loans, `duration_days` and `overdue_days` are
 * computed against the current time so the UI always shows up-to-date figures
 * without requiring a page refresh. Borrower names are batch-fetched using
 * deduplicated user IDs.
 *
 * Uses a 5-minute `staleTime` because loan history is append-only — new
 * entries are infrequent relative to reads.
 *
 * @param itemId - The UUID of the item, or `null` to disable the query.
 * @returns A TanStack `UseQueryResult` resolving to an array of {@link LoanHistoryEntry} objects
 *   sorted newest-first.
 */
export function useLoanHistory(itemId: string | null) {
  return useQuery({
    queryKey: ['loan-history', itemId],
    queryFn: async (): Promise<LoanHistoryEntry[]> => {
      const { data: loans, error } = await supabase
        .from('loans')
        .select('id, borrower_id, borrowed_at, returned_at, due_at, notes, return_condition, return_notes')
        .eq('item_id', itemId!)
        .order('borrowed_at', { ascending: false });

      if (error) throw error;
      if (!loans || loans.length === 0) return [];

      // Batch-fetch borrower display names using deduplicated IDs.
      const borrowerIds = [...new Set(loans.map((l) => l.borrower_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', borrowerIds);

      const profileMap = new Map(profiles?.map((p) => [p.user_id, p.display_name]) || []);

      return loans.map((loan) => {
        // Use the return date if available; otherwise use now (loan still active).
        const endDate = loan.returned_at ? new Date(loan.returned_at) : new Date();
        const startDate = new Date(loan.borrowed_at);
        const durationDays = calcLoanDurationDays(startDate, endDate);

        // Overdue: how many days past the due date the item was/is returned.
        let overdueDays: number | null = null;
        if (loan.due_at) {
          const deadlineDate = new Date(loan.due_at);
          const compareDate = loan.returned_at ? new Date(loan.returned_at) : new Date();
          if (compareDate > deadlineDate) {
            const overdueMs = compareDate.getTime() - deadlineDate.getTime();
            overdueDays = Math.ceil(overdueMs / (1000 * 60 * 60 * 24));
          }
        }

        return {
          id: loan.id,
          borrower_id: loan.borrower_id,
          borrower_name: profileMap.get(loan.borrower_id) || null,
          borrowed_at: loan.borrowed_at,
          returned_at: loan.returned_at,
          duration_days: durationDays,
          due_at: loan.due_at || null,
          overdue_days: overdueDays,
          notes: loan.notes ?? null,
          return_condition: loan.return_condition ?? null,
          return_notes: loan.return_notes ?? null,
        };
      });
    },
    enabled: !!itemId,
    staleTime: 5 * 60 * 1000, // 5 minutes — loan history is append-only
  });
}
